import { useCallback, useMemo, useState } from 'react';
import { Connection } from '@solana/web3.js';
import axios from 'axios';
import { formatUnits } from 'ethers/lib/utils';
import * as anchor from '@project-serum/anchor';
import { AnchorWallet } from '@solana/wallet-adapter-react';
import {
  AnchorProvider,
  Program as AnchorProgram,
  AnchorError,
} from '@project-serum/anchor';
import {
  ClaimRequestErrorCode,
  ClaimRequestStatusCode,
  getServerInfo,
  getUserClaimStatusKey,
  IClaimInfo,
  parseClaimInfo,
} from '../utils/claimHelper';
import { useSolanaWallet } from '../contexts/SolanaWalletContext';
import {getTestWallet, IS_LOCAL_TEST, SOLANA_HOST} from '../utils/consts';
import ConsoleHelper from '../utils/consoleHelper';
import {
  CGC_TOKEN_CLAIM_PROGRAM_ID,
  URL_CLAIM_INFO_V2,
} from '../utils/solchickConsts';
import { pubkeyToString, toPublicKey } from '../utils/solanaHelper';
import { IDL, CgcTokenClaim } from '../anchor/cgc_token_claim';

const claimProgramId = toPublicKey(CGC_TOKEN_CLAIM_PROGRAM_ID);
interface IClaimInfoStatus {
  isProcessing: boolean;
  claimInfoResult: IClaimInfo;
  statusCode: ClaimRequestStatusCode;
  errorCode: ClaimRequestErrorCode;
  lastError: string | null;
  claimInfo(): void;
}

const createClaimInfoStatus = (
  claimInfo: () => void,
  isProcessing: boolean,
  claimInfoResult: IClaimInfo,
  statusCode = ClaimRequestStatusCode.NONE,
  errorCode: ClaimRequestErrorCode,
  lastError: string | null,
) => ({
  claimInfo,
  isProcessing,
  claimInfoResult,
  statusCode,
  errorCode,
  lastError,
});

function useClaimInfo(): IClaimInfoStatus {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusCode, setStatusCode] = useState(ClaimRequestStatusCode.NONE);
  const [errorCode, setErrorCode] = useState(ClaimRequestErrorCode.NO_ERROR);
  const [lastError, setLastError] = useState('');
  const [claimInfoResult, setClaimInfoResult] = useState({
    totalAllocationAmount: 0,
    totalClaimedAmount: 0,
    claimableAmount: 0,
    unvestedAmount: 0,
    nextUnlockDate: null,
    nextUnlockAmount: 0,
  } as IClaimInfo);
  const walletSolana = useSolanaWallet();

  const solanaConnection = useMemo(
    () => new Connection(SOLANA_HOST, 'confirmed'),
    [],
  );

  const getAnchorProvider = useCallback(
    async () =>
      new AnchorProvider(
        solanaConnection,
        walletSolana as unknown as AnchorWallet,
        {
          preflightCommitment: `processed`,
          commitment: `confirmed`,
        },
      ),
    [solanaConnection, walletSolana],
  );

  const setError = (error: ClaimRequestErrorCode) => {
    ConsoleHelper(`setError: ${error}`);
    setStatusCode(ClaimRequestStatusCode.FAILED);
    setErrorCode(error);
    setIsProcessing(false);
  };

  const submitClaimInfoRequest = async (address: string) => {
    if (IS_LOCAL_TEST) {
      if (getTestWallet()) {
        address = getTestWallet();
      }
    }
    const url = URL_CLAIM_INFO_V2(address);
    if (!walletSolana.publicKey || !solanaConnection) {
      return;
    }
    const provider = await getAnchorProvider();
    if (!provider) {
      return;
    }
    const anchorProgram = new AnchorProgram<CgcTokenClaim>(
      IDL,
      claimProgramId,
      provider,
    );
    ConsoleHelper(`submitClaimInfoRequest: ${url}`);
    axios.get(url).then(
      async (results) => {
        if (!walletSolana.publicKey) {
          return;
        }
        if (results.data.success) {
          const userInfo = results.data.data;
          const [userClaimStatusKey] = await getUserClaimStatusKey(
            claimProgramId,
            walletSolana.publicKey,
          );

          let bnClaimedAmount = new anchor.BN(0);
          try {
            const userClaimStatus =
              await anchorProgram.account.userClaimStatusAccount.fetchNullable(
                userClaimStatusKey,
              );
            if (userClaimStatus) {
              bnClaimedAmount = userClaimStatus.claimedAmount;
            }
          } catch (e) {}
          const parsedResult: IClaimInfo = parseClaimInfo(
            userInfo,
            bnClaimedAmount,
          );
          ConsoleHelper(`setClaimInfoResult: `, parsedResult);
          setClaimInfoResult(parsedResult);
          setIsProcessing(false);
          setStatusCode(ClaimRequestStatusCode.SUCCESS);
        } else {
          const errorMessage = results.data.error_message || 'Unknown error';
          setLastError(
            `${errorMessage} (Error code: ${results.data.error_code})`,
          );
          setError(ClaimRequestErrorCode.SUBMIT_FAILED);
        }
      },
      (error) => {
        ConsoleHelper(`submitClaimInfoRequest: ${error}`);
        setLastError(`Unknown error`);
        setError(ClaimRequestErrorCode.SUBMIT_FAILED);
      },
    );
  };

  const getClaimInfo = async () => {
    // noinspection DuplicatedCode
    const { publicKey: walletPublicKey } = walletSolana;
    if (!walletPublicKey || !solanaConnection) {
      return false;
    }

    setIsProcessing(true);
    setStatusCode(ClaimRequestStatusCode.START);

    const ret = await getServerInfo();
    if (!ret) {
      setError(ClaimRequestErrorCode.SERVER_INVALID);
      return false;
    }

    setStatusCode(ClaimRequestStatusCode.SUBMITTING);
    await submitClaimInfoRequest(pubkeyToString(walletPublicKey));
    return true;
  };

  const claimInfo = async () => {
    ConsoleHelper('claimInfo: start');
    await getClaimInfo();
  };

  return createClaimInfoStatus(
    claimInfo,
    isProcessing,
    claimInfoResult,
    statusCode,
    errorCode,
    lastError,
  );
}

export default useClaimInfo;
